import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Phone, Mail, Calendar, Briefcase, Award, FileText, ChevronRight, Edit3, LogOut } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { VerificationBadge } from '../components/ui/VerificationBadge';
import { TrustScore } from '../components/ui/TrustScore';
import { useScreenInit } from '../useScreenInit';
export function Profile() {
  useScreenInit();
  const navigate = useNavigate();
  const handleLogout = () => {
    // Clear session here in a real app, then return to the auth flow
    navigate('/welcome', {
      replace: true
    });
  };
  const user = {
    name: 'Amina Diallo',
    role: 'Agricultural Entrepreneur',
    location: 'Kano, Nigeria',
    joined: 'March 2023',
    phone: '+234 800 123 4567',
    email: 'amina.d@example.com',
    avatar: 'https://images.unsplash.com/photo-1531123897727-8f129e1bf98c?q=80&w=200&h=200&auto=format&fit=crop',
    trustScore: 85,
    business: {
      name: 'Green Harvest Farms',
      stage: 'Growth',
      description: 'Sustainable poultry and vegetable farming supplying local markets and schools.'
    },
    skills: ['Poultry Management', 'Crop Rotation', 'Basic Accounting', 'Digital Marketing']
  };
  return <div className="bg-brand-background min-h-screen pb-6" data-id="element-439">
      {/* Hero Section */}
      <div className="bg-brand-primary pt-12 pb-24 px-4 relative" data-id="element-440">
        <div className="absolute top-4 right-4" data-id="element-441">
          <button className="p-2 bg-white/20 rounded-full text-white backdrop-blur-sm hover:bg-white/30 transition-colors" data-id="element-442">
            <Edit3 className="w-5 h-5" data-id="element-443" />
          </button>
        </div>
      </div>

      <div className="px-4 sm:px-6 -mt-20 space-y-4" data-id="element-444">
        {/* Profile Card */}
        <Card className="relative pt-16 pb-6 px-6 text-center" data-id="element-445">
          <div className="absolute -top-16 left-1/2 -translate-x-1/2" data-id="element-446">
            <div className="relative" data-id="element-447">
              <img src={user.avatar} alt={user.name} className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-md bg-white" data-id="element-448" />
              <div className="absolute bottom-0 right-0 bg-white rounded-full p-1 shadow-sm" data-id="element-449">
                <VerificationBadge level="verified" showLabel={false} className="w-8 h-8 p-0 justify-center" data-id="element-450" />
              </div>
            </div>
          </div>

          <h1 className="text-2xl font-bold text-text-primary mb-1" data-id="element-451">
            {user.name}
          </h1>
          <p className="text-brand-primary font-medium mb-3" data-id="element-452">{user.role}</p>

          <div className="flex items-center justify-center text-text-secondary text-sm mb-4 space-x-4" data-id="element-453">
            <span className="flex items-center" data-id="element-454">
              <MapPin className="w-4 h-4 mr-1" data-id="element-455" /> {user.location}
            </span>
            <span className="flex items-center" data-id="element-456">
              <Calendar className="w-4 h-4 mr-1" data-id="element-457" /> Joined {user.joined}
            </span>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-6" data-id="element-458">
            <VerificationBadge level="verified" data-id="element-459" />
            <VerificationBadge level="community" data-id="element-460" />
            <VerificationBadge level="certified" data-id="element-461" />
          </div>

          <div className="flex justify-center items-center space-x-6 py-4 border-t border-gray-100" data-id="element-462">
            <div className="text-center" data-id="element-463">
              <p className="text-sm text-text-secondary mb-1" data-id="element-464">Trust Score</p>
              <TrustScore score={user.trustScore} size={56} strokeWidth={6} data-id="element-465" />
            </div>
            <div className="h-12 w-px bg-gray-200" data-id="element-466"></div>
            <div className="text-center" data-id="element-467">
              <p className="text-sm text-text-secondary mb-1" data-id="element-468">Status</p>
              <p className="font-bold text-brand-success" data-id="element-469">Active</p>
            </div>
          </div>
        </Card>

        {/* Business Information */}
        <Card data-id="element-470">
          <div className="flex items-center mb-4" data-id="element-471">
            <Briefcase className="w-5 h-5 text-brand-primary mr-2" data-id="element-472" />
            <h2 className="text-lg font-bold text-text-primary" data-id="element-473">
              Business Overview
            </h2>
          </div>
          <div className="space-y-3" data-id="element-474">
            <div data-id="element-475">
              <p className="text-xs text-text-secondary uppercase tracking-wider font-semibold mb-1" data-id="element-476">
                Business Name
              </p>
              <p className="font-medium text-text-primary" data-id="element-477">
                {user.business.name}
              </p>
            </div>
            <div data-id="element-478">
              <p className="text-xs text-text-secondary uppercase tracking-wider font-semibold mb-1" data-id="element-479">
                Stage
              </p>
              <span className="inline-block px-2.5 py-1 bg-brand-accent/10 text-brand-accent text-xs font-semibold rounded-lg" data-id="element-480">
                {user.business.stage}
              </span>
            </div>
            <div data-id="element-481">
              <p className="text-xs text-text-secondary uppercase tracking-wider font-semibold mb-1" data-id="element-482">
                Description
              </p>
              <p className="text-sm text-text-primary leading-relaxed" data-id="element-483">
                {user.business.description}
              </p>
            </div>
          </div>
        </Card>

        {/* Skills */}
        <Card data-id="element-484">
          <div className="flex items-center mb-4" data-id="element-485">
            <Award className="w-5 h-5 text-brand-secondary mr-2" data-id="element-486" />
            <h2 className="text-lg font-bold text-text-primary" data-id="element-487">
              Skills & Certifications
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-488">
            {user.skills.map((skill, idx) => <span key={idx} className="px-3 py-1.5 bg-gray-100 text-text-primary text-sm font-medium rounded-xl" data-id="element-489">
                {skill}
              </span>)}
          </div>
        </Card>

        {/* Contact Info */}
        <Card data-id="element-490">
          <h2 className="text-lg font-bold text-text-primary mb-4" data-id="element-491">
            Contact Information
          </h2>
          <div className="space-y-4" data-id="element-492">
            <div className="flex items-center" data-id="element-493">
              <div className="w-10 h-10 rounded-full bg-brand-background flex items-center justify-center text-text-secondary mr-3" data-id="element-494">
                <Phone className="w-5 h-5" data-id="element-495" />
              </div>
              <div data-id="element-496">
                <p className="text-sm text-text-secondary" data-id="element-497">Phone</p>
                <p className="font-medium text-text-primary" data-id="element-498">{user.phone}</p>
              </div>
            </div>
            <div className="flex items-center" data-id="element-499">
              <div className="w-10 h-10 rounded-full bg-brand-background flex items-center justify-center text-text-secondary mr-3" data-id="element-500">
                <Mail className="w-5 h-5" data-id="element-501" />
              </div>
              <div data-id="element-502">
                <p className="text-sm text-text-secondary" data-id="element-503">Email</p>
                <p className="font-medium text-text-primary" data-id="element-504">{user.email}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Documents Link */}
        <Card interactive className="flex items-center justify-between" data-id="element-505">
          <div className="flex items-center" data-id="element-506">
            <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center text-brand-primary mr-3" data-id="element-507">
              <FileText className="w-5 h-5" data-id="element-508" />
            </div>
            <div data-id="element-509">
              <p className="font-bold text-text-primary" data-id="element-510">
                Documents & Evidence
              </p>
              <p className="text-xs text-text-secondary" data-id="element-511">
                View uploaded files and photos
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-text-secondary" data-id="element-512" />
        </Card>

        {/* Log Out */}
        <button type="button" onClick={handleLogout} className="w-full flex items-center justify-center gap-2 py-3.5 mt-2 rounded-xl border-2 border-brand-danger/20 text-brand-danger font-semibold bg-white hover:bg-brand-danger/5 focus:outline-none focus:ring-2 focus:ring-brand-danger transition-colors active:scale-[0.98]" data-id="element-513">
          <LogOut className="w-5 h-5" data-id="element-514" />
          Log Out
        </button>
      </div>
    </div>;
}